/**
 * TilePile - clickable pile for flipping vowels or consonants
 */

import { motion } from 'framer-motion';
import { useGameStore, selectIsMyTurn } from '../../stores/gameStore';
import { socketActions } from '../../lib/socketActions';
import { useSettingsStore } from '../../stores/settingsStore';
import { triggerHaptic } from '../../utils/haptics';
import clsx from 'clsx';

interface TilePileProps {
  type: 'vowel' | 'consonant';
}

export function TilePile({ type }: TilePileProps) {
  const count = useGameStore((s) =>
    type === 'vowel' ? s.gameState?.vowelsRemaining ?? 0 : s.gameState?.consonantsRemaining ?? 0
  );
  const isMyTurn = useGameStore(selectIsMyTurn);
  const isPaused = useGameStore((s) => s.gameState?.isPaused ?? false);
  const isFlippingTile = useGameStore((s) => s.isFlippingTile);
  const reducedMotion = useSettingsStore((s) => s.reducedMotion);

  const isEmpty = count === 0;
  const canFlip = isMyTurn && !isEmpty && !isFlippingTile && !isPaused;

  const handleClick = () => {
    if (!canFlip) return;

    triggerHaptic('medium');
    socketActions.flipTile(type);
  };

  return (
    <motion.button
      onClick={handleClick}
      disabled={!canFlip}
      className={clsx(
        'tile-pile-btn touch-target flex-1 sm:flex-none flex flex-row sm:flex-col gap-2 sm:gap-0 items-center justify-center p-2 sm:p-3 rounded-lg transition-all',
        type === 'vowel'
          ? 'bg-slate-800 text-white'
          : 'bg-white dark:bg-slate-700 text-slate-800 dark:text-white border-2 border-slate-200 dark:border-slate-600',
        canFlip
          ? 'cursor-pointer hover:scale-105'
          : 'opacity-50 cursor-not-allowed',
        isEmpty && 'opacity-30'
      )}
      whileTap={canFlip && !reducedMotion ? { scale: 0.95 } : undefined}
      aria-label={`Flip ${type}. ${count} remaining.${isMyTurn ? ' Your turn!' : ''}${isFlippingTile ? ' Flipping...' : ''}`}
    >
      <div className="text-xs sm:text-sm font-bold sm:mb-0.5">
        {type === 'vowel' ? 'Vowels' : 'Consonants'}
      </div>
      <div className="text-lg sm:text-2xl font-mono">{count}</div>
      {canFlip && (
        <motion.div
          className="text-[10px] sm:text-xs mt-0.5 opacity-80 hidden sm:block"
          animate={{ opacity: [0.5, 1, 0.5] }}
          transition={{ duration: 1.5, repeat: Infinity }}
        >
          Click to flip
        </motion.div>
      )}
    </motion.button>
  );
}
